import React, { ReactElement, useState } from 'react';
import {
  Image, Pressable, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import EditIcon24 from '../24pxIcons/EditIcon24';
import EditUser from './EditUser';
import CheckBox from '../16pxIcons/CheckBox';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import AlertModal from '../AlertModal';
import deleteUser from '../../requests/Users/DeleteUser';
import { logEvent } from '../LogEvent';
import ReInviteIcon24 from '../24pxIcons/ReInviteIcon24';
import { PressableState } from '../PressableStyled';
import Style from '../../constants/Style';
import reinviteUser from '../../requests/Users/ReinviteUser';
import RoleModal from './RoleModal';
import { TAG_COLORS, UserPermission } from '../../constants/Types';
import updateUserGroupUsers from '../../requests/Users/UpdateUserGroupUsers';

interface UserRowWebProps {
  user,
  selectable?: boolean,
  // eslint-disable-next-line no-unused-vars
  showError?: (message?: string) => void,
  userDeleted?: () => void,
  roleChanged?: () => void,
  roles?: object[],
  userGroups?: { Name: string, UserGroupId: string }[],
  selectedGroupId?: string,
}

export default function UserRowWeb({
  user, selectable, showError, userDeleted, roleChanged, roles, userGroups, selectedGroupId,
}: UserRowWebProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const canUpdateUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_UPDATE);
  const canDeleteUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_DELETE);
  const canViewRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_READ);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [roleModalVisible, setRoleModalVisible] = useState<boolean>(false);
  const [selected, setSelected] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [reinviteModalVisible, setReinviteModalVisible] = useState<boolean>(false);

  const generateColor = (first: number, last: number) => {
    const randomColor = Math.floor((1 / (first + last)) * 16777215)
      .toString(16)
      .padStart(6, '0');
    return `#${randomColor}`;
  };

  const reinvite = () => {
    reinviteUser(user.email).then(() => setReinviteModalVisible(true))
      .catch(() => showError(''));
  };

  const renderUserIcon = (imageSource: string, facilityManager: boolean = false) => {
    if (imageSource === undefined) {
      return (
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            marginRight: 8,
            backgroundColor: generateColor(
              user?.firstName ? user?.firstName?.length : user?.email?.length,
              user?.lastName ? user?.lastName?.length : user?.email?.length,
            ),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              color: '#fff',
              fontFamily: 'Roboto_400Regular',
              fontSize: 8,
            }}
            selectable
          >
            {`${(user?.firstName ? user?.firstName : user?.email)?.charAt(0)?.toUpperCase() ?? ''}${user?.lastName?.charAt(0)?.toUpperCase() ?? ''}`}
          </Text>
        </View>
      );
    }
    return (
      <View
        style={{
          width: 48,
          height: 48,
          backgroundColor: facilityManager ? '#fff' : 'transparent',
          borderWidth: 1.5,
          borderColor: facilityManager ? Colors.light.hereworksBlue500 : 'transparent',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 24,
          marginLeft: -14,
        }}
      >
        <Image
          style={styles.userPic}
          source={{ uri: imageSource }}
        />
      </View>
    );
  };

  const pending: boolean = user.status === 'PENDING_VERIFICATION';

  const userName: string = (user.firstName || user.lastName)
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}` : user.email;

  const getRoleTitle = (): string => {
    if (user.roleList?.length) {
      return user.roleList[0].name;
    }
    return user.isAdmin ? 'Admin' : 'Non Admin';
  };

  return (
    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
      <EditUser
        editModalVisible={editModalVisible}
        {...{ user, roleChanged, setEditModalVisible }}
        imageSource={user.icon}
        facilityManager={user.facilityManager}
        roles={roles}
      />
      <View
        style={{
          marginLeft: 22,
          flex: 1,
          flexDirection: 'row',
          marginVertical: 8,
          alignItems: 'center',
        }}
      >
        {selectable && (
          <TouchableOpacity
            onPress={() => setSelected(!selected)}
            style={{ marginRight: 30, justifyContent: 'center' }}
          >
            <CheckBox
              checkcolor={selected ? null : '#fff'}
              color={selected ? null : Colors.light.hereworksBlue100}
            />
          </TouchableOpacity>
        )}
        {renderUserIcon(user.icon, user.facilityManager)}
        <View style={{ marginLeft: 4, flex: 1 }}>
          <Text numberOfLines={1} style={styles.nameStyle}>
            {userName}
          </Text>
        </View>
      </View>
      <View style={{ flex: 1 }}>
        <Text style={[styles.userDetails, { marginLeft: 34 }]} selectable>{user.email}</Text>
      </View>
      <View style={{ flex: 1 }}>
        {(user.roleList?.length) ? <>
          {user.roleList.map((role, index) => (
            <Pressable style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-start',
              backgroundColor: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].background,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 65,
              marginLeft: 48,
            }}
              onPress={() => setRoleModalVisible(true)}
              disabled={!canViewRoles}
            >
              <Text key={role.id} style={{
                fontFamily: 'Poppins_400Regular',
                fontSize: 12,
                backgroundColor: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].background,
                color: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].text,
              }}>
                {role.name}{index < user.roleList.length - 1 ? ', ' : ''}
              </Text>
            </Pressable>
          ))}</> : <Pressable
            onPress={() => setRoleModalVisible(true)}
            disabled={!canViewRoles}
          >
          <Text style={[styles.userDetails, {
            alignSelf: 'flex-start',
            marginLeft: 48,
            fontFamily: 'Poppins_400Regular',
            fontSize: 12,
            backgroundColor: Colors.light.hereworksBlue50,
            color: Colors.light.hereworksBlue600,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 65,
          }]}>
            {user.isAdmin ? 'Admin' : 'Non Admin'}
          </Text></Pressable>}
      </View>
      <View style={[styles.statusLabelContainer, pending && styles.statusPending]}>
        <Text style={[
          styles.statusLabel,
          {
            color: pending ? styles.statusPending.borderColor
              : styles.statusLabelContainer.borderColor,
          },
        ]}>
          {pending ? 'Pending' : 'Accepted'}
        </Text>
      </View>
      {!(canUpdateUsers || canDeleteUsers) && <View style={{ flex: 1 }}>
        <Text style={[styles.userDetails, { alignSelf: 'flex-end', marginLeft: 34 }]}>
          {Moment(user.LastAuthenticatedOn).format('ll')}
        </Text>
      </View>}
      <View style={{ flex: 1 }}>
        <View style={styles.buttonContainer}>
          <>
            {pending && <Pressable
              onPress={() => {
                reinvite();
                logEvent('UserRowWeb', 'Re-invite');
              }}
              testID='um-re-invite-user-role-btn'
            >
              {({ hovered }: PressableState): ReactElement => (
                <View
                  style={{
                    backgroundColor: hovered ? Colors.light.hereworksBlue500 : 'transparent',
                    borderRadius: 4,
                  }}
                >
                  <ReInviteIcon24 color={hovered ? '#fff' : null} />
                </View>
              )}
            </Pressable>}
            {canUpdateUsers && <Pressable
              style={{ marginLeft: 12 }}
              onPress={() => {
                setEditModalVisible(true);
                logEvent('UserRowWeb', 'Edit');
              }}
              testID='um-edit-user-role-btn'
            >
              {({ hovered }: PressableState): ReactElement => <View
                style={{
                  backgroundColor: hovered ? Colors.light.hereworksBlue500 : 'transparent',
                  borderRadius: 4,
                }}
              >
                <EditIcon24 color={hovered ? '#fff' : null} />
              </View>}
            </Pressable>}
            {canDeleteUsers && <Pressable
              style={{ marginLeft: 12 }}
              onPress={() => {
                logEvent('UserRowWeb', 'Delete');
                setDeleteModalVisible(true);
              }}
              testID='um-delete-user-btn'
            >
              {({ hovered }: PressableState): ReactElement => <View
                style={{
                  backgroundColor: hovered ? Colors.light.hereworksRed500 : 'transparent',
                  borderRadius: 4,
                }}
              >
                <DeleteIcon24 color={hovered ? '#fff' : null} />
              </View>}
            </Pressable>}
          </>
        </View>
      </View>
      <AlertModal
        modalVisible={deleteModalVisible}
        setModalVisible={setDeleteModalVisible}
        title={selectedGroupId ? 'Remove User from Group' : 'Delete User'}
        message={
          <>
            {`Are you sure you want to ${selectedGroupId ? 'remove' : 'delete'} the user `}
            <Text style={{ fontFamily: 'Roboto_700Bold' }}>{userName}</Text>
            {'?'}
          </>
        }
        actionTitle={selectedGroupId ? 'Remove' : 'Delete'}
        action={() => {
          if (selectedGroupId) {
            updateUserGroupUsers(
              selectedGroupId,
              [],
              [user.id],
              userGroups?.find((group) => group.UserGroupId === selectedGroupId)?.Name ?? '',
              buildingId,
            ).then((res) => {
              if (res?.isError) {
                showError(res?.message);
              } else {
                userDeleted();
              }
            });
            return;
          }
          deleteUser(user.email).then((res) => {
            if (res?.isError) {
              showError(res?.message);
            } else {
              userDeleted();
            }
          });
        }}
      />
      <AlertModal
        modalVisible={reinviteModalVisible}
        setModalVisible={setReinviteModalVisible}
        title={'Success!'}
        message={<>
          {'New invitation sent to '}
          <Text style={Style.alertBoldText}>{userName}</Text>
        </>}
        error={false}
      />
      <RoleModal
        permissions={user.roleList?.length ? user.roleList[0]?.permissions : null}
        isAdmin={user.isAdmin}
        isVisible={roleModalVisible}
        setModalVisible={setRoleModalVisible}
        title={getRoleTitle()}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  userDetails: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
  },
  nameStyle: {
    marginLeft: 10,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  userPic: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  statusLabelContainer: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksGreen500,
    backgroundColor: Colors.light.hereworksGreen50,
    paddingHorizontal: 8,
    start: 40,
    alignSelf: 'center',
  },
  statusLabel: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
  },
  statusPending: {
    borderColor: Colors.light.hereworksYellow900,
    backgroundColor: Colors.light.hereworksYellow50,
  },
  buttonContainer: {
    flexDirection: 'row',
    marginRight: 8,
    width: 96,
    alignSelf: 'flex-end',
    justifyContent: 'flex-end',
  },
});
